import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2, Sparkles, Loader2, ArrowLeft, Plane, Building2, Luggage, Car, Wallet,
  Check, AlertTriangle, Radar, MapPin, Star, ListOrdered,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { useI18n, verdictKey } from "../i18n";
import { toast } from "sonner";

export default function Evaluate() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const budget = Number(sessionStorage.getItem("dv_budget") || 2500);
  const [nl, setNl] = useState(t("evaluate.nlDefault"));
  const [parsing, setParsing] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [f, setF] = useState({
    destination: "", flights: 0, hotel: 0, bags: 0, transfers: 0,
    nights: 7, hotel_rating: 4.0, baggage_included: true,
  });

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const runParse = async () => {
    if (!nl.trim()) return;
    setParsing(true);
    try {
      const r = await api.evaluateParse(nl);
      setF((p) => ({ ...p, ...r.option }));
      toast.success(t("plan.understood"));
    } catch { toast.error(t("evaluate.parseErr")); } finally { setParsing(false); }
  };

  const runEvaluate = async () => {
    setEvaluating(true);
    setRanking(null);
    try {
      const [r, recs] = await Promise.all([api.evaluate({ ...f, budget }), api.recommendations({ budget })]);
      setResult(r);
      const yourName = r.destination || t("evaluate.yourOption");
      const picks = (recs.trips || []).map((tr) => ({ name: tr.destination, total: tr.total, decision: tr.decision, you: false }));
      const bestPick = picks[0] || null;
      const combined = [...picks, { name: yourName, total: r.total, decision: r.decision, you: true }]
        .sort((a, b) => a.total - b.total)
        .map((row, i) => ({ ...row, rank: i + 1 }));
      const yourRow = combined.find((row) => row.you);
      const diff = bestPick ? r.total - bestPick.total : 0;
      setRanking({ rows: combined, total: combined.length, yourRank: yourRow.rank, diff });
    } catch { toast.error(t("evaluate.parseErr")); } finally { setEvaluating(false); }
  };

  const addWatch = async () => {
    if (!result) return;
    setAdding(true);
    try {
      await api.addWatchCustom({ destination: result.destination, total: result.total, decision: result.decision });
      toast.success(t("evaluate.added"));
      navigate("/watch");
    } catch { toast.error("—"); } finally { setAdding(false); }
  };

  const NUM = [
    { k: "flights", label: t("evaluate.flights"), icon: Plane },
    { k: "hotel", label: t("evaluate.hotel"), icon: Building2 },
    { k: "bags", label: t("evaluate.bags"), icon: Luggage },
    { k: "transfers", label: t("evaluate.transfers"), icon: Car },
  ];

  const isBook = result?.decision === "BOOK";
  const c = isBook ? { main: "var(--book)", ink: "var(--book-ink)", soft: "var(--book-soft)" } : { main: "var(--wait)", ink: "var(--wait-ink)", soft: "var(--wait-soft)" };

  return (
    <Shell active="plan">
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-4 hover:text-slate-900 transition-colors"><ArrowLeft size={16} /> Decidova</button>
      <div className="mb-5">
        <div className="text-[11px] font-semibold tracking-[0.24em] font-display" style={{ color: "var(--brand)" }}>{t("evaluate.kicker")}</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("evaluate.title")}</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-xl">{t("evaluate.sub")}</p>
      </div>

      <div className="dv-card p-4 sm:p-5 mb-5">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-2"><Wand2 size={14} color="var(--brand)" /> {t("evaluate.nlLabel")}</label>
        <textarea value={nl} onChange={(e) => setNl(e.target.value)} data-testid="evaluate-nl-input" rows={2}
          className="w-full resize-none rounded-xl border border-[var(--line-strong)] bg-[var(--paper)] px-4 py-3 text-sm text-slate-800 outline-none focus:border-[var(--brand)] transition-colors" />
        <div className="mt-3 flex justify-end">
          <button onClick={runParse} disabled={parsing} data-testid="evaluate-parse-button" className="dv-btn dv-btn-ghost text-sm px-4 py-2">
            {parsing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} color="var(--brand)" />} {parsing ? t("plan.reading") : t("plan.fillFromText")}
          </button>
        </div>
      </div>

      <div className="dv-card p-4 sm:p-5 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 flex items-center gap-2">
          <MapPin size={15} color="var(--brand)" />
          <input value={f.destination} onChange={(e) => upd("destination", e.target.value)} data-testid="eval-destination" placeholder={t("evaluate.destination")} className="flex-1 rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
        </div>
        {NUM.map((n) => (
          <div key={n.k} className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
            <span className="inline-flex items-center gap-2 text-sm text-slate-600"><n.icon size={15} color="var(--muted)" /> {n.label}</span>
            <div className="flex items-center gap-1">
              <span className="text-slate-400 text-sm">€</span>
              <input type="number" value={f[n.k]} onChange={(e) => upd(n.k, Number(e.target.value))} data-testid={`eval-${n.k}`} className="w-20 text-right font-mono-num text-sm bg-transparent outline-none" />
            </div>
          </div>
        ))}
        <label className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
          <span className="inline-flex items-center gap-2 text-sm text-slate-600"><Luggage size={15} color="var(--muted)" /> {t("evaluate.baggage")}</span>
          <input type="checkbox" checked={f.baggage_included} onChange={(e) => upd("baggage_included", e.target.checked)} data-testid="eval-baggage" className="w-4 h-4 accent-[var(--book)]" />
        </label>
        <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
          <span className="inline-flex items-center gap-2 text-sm text-slate-600"><Star size={15} color="var(--muted)" /> {t("evaluate.rating")}</span>
          <input type="number" step="0.5" min="1" max="5" value={f.hotel_rating} onChange={(e) => upd("hotel_rating", Number(e.target.value))} data-testid="eval-rating" className="w-16 text-right font-mono-num text-sm bg-transparent outline-none" />
        </div>
      </div>

      <button onClick={runEvaluate} disabled={evaluating} data-testid="evaluate-button" className="dv-btn dv-btn-primary w-full text-base py-4 mt-5">
        {evaluating ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />} {evaluating ? t("evaluate.evaluating") : t("evaluate.evaluateBtn")}
      </button>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="dv-card mt-6 overflow-hidden" data-testid="evaluate-result" style={{ borderColor: c.soft }}>
            <div className="p-5 sm:p-6">
              <div className="text-[11px] text-slate-400">{t("evaluate.resultFor")}</div>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
                <div>
                  <div className="font-display font-bold text-xl">{result.destination}</div>
                  <div className="font-display font-extrabold text-4xl mt-1" style={{ color: c.main }} data-testid="evaluate-verdict">{t(verdictKey(result.decision))}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-semibold tracking-[0.22em] text-slate-400 font-display">{t("common.total")}</div>
                  <div className="dv-total text-4xl text-slate-900">{eur(result.total)}</div>
                  <div className="text-xs mt-1" style={{ color: result.budget_left >= 0 ? "var(--book-ink)" : "var(--alert)" }}>
                    {result.budget_left >= 0 ? t("common.belowBudget", { x: eur(result.budget_left) }) : t("common.overBudget", { x: eur(-result.budget_left) })}
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-2">
                {result.reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: c.soft }}>
                      {isBook ? <Check size={12} color={c.ink} /> : <AlertTriangle size={11} color={c.ink} />}
                    </span>{r}
                  </li>
                ))}
              </ul>
              <button onClick={addWatch} disabled={adding} data-testid="evaluate-add-watch" className="dv-btn dv-btn-ghost w-full text-base py-3.5 mt-5">
                {adding ? <Loader2 size={16} className="animate-spin" /> : <Radar size={16} color="var(--brand)" />} {t("evaluate.addToWatch")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {ranking && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="dv-card mt-4 overflow-hidden" data-testid="evaluate-ranking">
          <div className="p-5 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><ListOrdered size={16} color="var(--brand)" /></div>
              <div className="font-display font-bold text-lg leading-tight">{t("evaluate.rankTitle")}</div>
            </div>
            <p className="text-sm text-slate-500 mt-1.5">{t("evaluate.rankSub")}</p>
            <div className="mt-3 dv-pill text-xs px-3 py-1.5 w-max" style={{ background: "var(--brand-soft)", color: "var(--brand)" }} data-testid="evaluate-rank-summary">
              {t("evaluate.ranksNth", { n: ranking.yourRank, total: ranking.total })}
              {ranking.diff !== 0 && (
                <span className="text-slate-500">· {ranking.diff > 0 ? t("evaluate.costlierThanBest", { x: eur(ranking.diff) }) : t("evaluate.cheaperThanBest", { x: eur(-ranking.diff) })}</span>
              )}
              {ranking.diff === 0 && <span className="text-slate-500">· {t("evaluate.matchesBest")}</span>}
            </div>
          </div>
          <div>
            {ranking.rows.map((row) => (
              <div key={`${row.name}-${row.rank}`} data-testid={`rank-row-${row.rank}`}
                className="flex items-center gap-3 px-5 py-3 border-t border-[var(--line)]"
                style={{ background: row.you ? "var(--brand-soft)" : "#fff" }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold font-display shrink-0"
                  style={{ background: row.you ? "var(--brand)" : "var(--paper)", color: row.you ? "#fff" : "var(--muted)", border: row.you ? "none" : "1px solid var(--line)" }}>{row.rank}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                    {row.name}
                    {row.you && <span className="dv-pill text-[9px] px-1.5 py-0.5" style={{ background: "#fff", color: "var(--brand)" }}>{t("evaluate.yourOption")}</span>}
                  </div>
                </div>
                <span className="dv-pill text-[10px] px-2 py-0.5 shrink-0" style={{ background: row.decision === "BOOK" ? "var(--book-soft)" : "var(--wait-soft)", color: row.decision === "BOOK" ? "var(--book-ink)" : "var(--wait-ink)" }}>{t(verdictKey(row.decision))}</span>
                <span className="dv-total text-base text-slate-900 shrink-0 w-20 text-right">{eur(row.total)}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400"><Wallet size={13} /> {t("results.lastChecked")}</div>
    </Shell>
  );
}
